import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';
import { useDispatch, useSelector } from 'react-redux';
import { BaseModal } from 'components/UI';
import {
  Grid,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
  TablePagination,
  Button,
  Badge,
  Tooltip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import ErrorIcon from '@mui/icons-material/Error';
import { buildUrl } from 'utils';
import {
  changeBulkReservationsStatus,
  resetBulkReservationsStatus,
} from 'state/modules/reservations/actions';

const ModalState = Object.freeze({
  CONFIRM_REASSIGN: Symbol('CONFIRM_REASSIGN'),
  CONFIRM_CANCEL: Symbol('CONFIRM_CANCEL'),
  SUCCESS: Symbol('SUCCESS'),
  FAILED: Symbol('FAILED'),
});

const ChangeVehicleStatusBulkReservationsConflictModal = ({
  open,
  onClose,
  onCloseAll,
  vehicles = [],
  startTime,
  endTime,
  newStatus,
  offlineReason,
  description,
}) => {
  const dispatch = useDispatch();
  const [currentState, setCurrentState] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(5);

  const { fetchingBulkReservationsAction, fetchedBulkReservationsActionSuccess, errors } =
    useSelector((state) => state.reservations);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleAction = (action) => {
    const payload = {
      action,
      vehicle_ids: vehicles.map((v) => v.id),
      start_time: startTime,
      end_time: endTime || null,
      vehicle: {
        status: newStatus,
        offline_reason: offlineReason,
        description: description,
      },
    };
    dispatch(changeBulkReservationsStatus(payload));
  };

  const confirmAction = (action) => {
    if (action === 'CANCEL') {
      setCurrentState(ModalState.CONFIRM_CANCEL);
    } else if (action === 'REASSIGN') {
      setCurrentState(ModalState.CONFIRM_REASSIGN);
    }
  };

  const handleClose = () => {
    dispatch(resetBulkReservationsStatus());
    onCloseAll();
  };

  useEffect(() => {
    if (fetchedBulkReservationsActionSuccess) {
      setCurrentState(ModalState.SUCCESS);
    }
  }, [fetchedBulkReservationsActionSuccess]);

  useEffect(() => {
    if (errors && !fetchedBulkReservationsActionSuccess && !fetchingBulkReservationsAction) {
      setCurrentState(ModalState.FAILED);
    }
  }, [errors, fetchedBulkReservationsActionSuccess, fetchingBulkReservationsAction]);

  const isResultModal = currentState === ModalState.SUCCESS || currentState === ModalState.FAILED;

  return (
    <>
      {!isResultModal && (
        <BaseModal
          size="xl"
          open={open}
          handleClick={onClose}
          enableCloseButton={true}
          title={i18n.t('modals.conflicting_reservations.title')}
        >
          <Grid container>
            <Grid item xs={12}>
              <Typography variant="body1" gutterBottom>
                {i18n.t('modals.conflicting_reservations.description_1')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {i18n.t('modals.conflicting_reservations.description_2')}
              </Typography>
            </Grid>
            <Grid size={12} sx={{ mt: 4, mb: 2 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18n.t('vehicle.vehicle_id_label')}</TableCell>
                      <TableCell>{i18n.t('vehicle.license_plate')}</TableCell>
                      <TableCell>{i18n.t('vehicle.vehicle_type_label')}</TableCell>
                      <TableCell>{i18n.t('vehicle.service_group')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vehicles
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <Link
                              target="_blank"
                              rel="noopener"
                              href={buildUrl(`/vehicles/${row.id}`)}
                            >
                              {row.id}
                            </Link>
                          </TableCell>
                          <TableCell>{row.license_plate}</TableCell>
                          <TableCell>
                            {row.vehicle_type?.make} {row.vehicle_type?.model}{' '}
                            {row.vehicle_type?.year}
                          </TableCell>
                          <TableCell>{row.vehicle_group?.name}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={vehicles.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
              />
            </Grid>
            <Grid container item xs={12} spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Grid item>
                <Button
                  disabled={fetchingBulkReservationsAction}
                  onClick={() => confirmAction('CANCEL')}
                >
                  {i18n.t('modals.conflicting_reservations.cancel_reservations')}
                </Button>
              </Grid>
              <Grid item>
                <Badge
                  overlap="rectangular"
                  badgeContent={
                    <Tooltip title={i18n.t('modals.conflicting_reservations.reassign_tooltip')}>
                      <InfoIcon color="primary" fontSize="small" />
                    </Tooltip>
                  }
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <Button
                    disabled={fetchingBulkReservationsAction}
                    onClick={() => confirmAction('REASSIGN')}
                  >
                    {i18n.t('modals.conflicting_reservations.reassign_reservations')}
                  </Button>
                </Badge>
              </Grid>
            </Grid>
          </Grid>
        </BaseModal>
      )}

      {currentState === ModalState.CONFIRM_CANCEL && (
        <BaseModal
          size="md"
          enableCloseButton={false}
          open={currentState === ModalState.CONFIRM_CANCEL}
          onClose={() => setCurrentState(null)}
        >
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{ maxWidth: 400, mx: 'auto' }}
          >
            <Grid item>
              <LibraryAddCheckIcon color="primary" />
            </Grid>
            <Grid item>
              <Typography variant="h6" color="primary" align="center">
                {i18n.t('modals.conflicting_reservations.confirm_action')}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" align="center">
                {i18n.t('modals.conflicting_reservations.confirm_cancel_description')}
              </Typography>
            </Grid>
            <Grid item container justifyContent="center" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setCurrentState(null)}
                sx={{ mr: 2 }}
              >
                {i18n.t('modals.conflicting_reservations.cancel')}
              </Button>
              <Button variant="contained" color="primary" onClick={() => handleAction('CANCEL')}>
                {i18n.t('modals.conflicting_reservations.confirm')}
              </Button>
            </Grid>
          </Grid>
        </BaseModal>
      )}

      {currentState === ModalState.CONFIRM_REASSIGN && (
        <BaseModal
          size="md"
          enableCloseButton={false}
          open={currentState === ModalState.CONFIRM_REASSIGN}
          onClose={() => setCurrentState(null)}
        >
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{ maxWidth: 400, mx: 'auto' }}
          >
            <Grid item>
              <LibraryAddCheckIcon color="primary" />
            </Grid>
            <Grid item>
              <Typography variant="h6" color="primary" align="center">
                {i18n.t('modals.conflicting_reservations.confirm_action')}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" align="center">
                {i18n.t('modals.conflicting_reservations.confirm_reassign_desc_1')}{' '}
                <b>{i18n.t('modals.conflicting_reservations.confirm_reassign_desc_2')}</b>
              </Typography>
            </Grid>
            <Grid item container justifyContent="center" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setCurrentState(null)}
                sx={{ mr: 2 }}
              >
                {i18n.t('modals.conflicting_reservations.cancel')}
              </Button>
              <Button variant="contained" color="primary" onClick={() => handleAction('REASSIGN')}>
                {i18n.t('modals.conflicting_reservations.confirm')}
              </Button>
            </Grid>
          </Grid>
        </BaseModal>
      )}

      {currentState === ModalState.SUCCESS && (
        <BaseModal size="md" open={currentState === ModalState.SUCCESS} handleClick={handleClose}>
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{ maxWidth: 400, mx: 'auto' }}
          >
            <Grid item>
              <LibraryAddCheckIcon color="primary" />
            </Grid>
            <Grid item>
              <Typography variant="h6" color="primary" align="center">
                {i18n.t('modals.conflicting_reservations.success_title')}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" align="center">
                {i18n.t('modals.conflicting_reservations.success_message')}
              </Typography>
            </Grid>
          </Grid>
        </BaseModal>
      )}

      {currentState === ModalState.FAILED && (
        <BaseModal size="md" open={currentState === ModalState.FAILED} handleClick={handleClose}>
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{ maxWidth: 400, mx: 'auto' }}
          >
            <Grid item>
              <ErrorIcon color="error" />
            </Grid>
            <Grid item>
              <Typography variant="h6" color="error" align="center">
                {i18n.t('modals.conflicting_reservations.failed_title')}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" align="center">
                {errors?.message || i18n.t('modals.conflicting_reservations.failed_message')}
              </Typography>
            </Grid>
          </Grid>
        </BaseModal>
      )}
    </>
  );
};

ChangeVehicleStatusBulkReservationsConflictModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCloseAll: PropTypes.func.isRequired,
  vehicles: PropTypes.array,
  startTime: PropTypes.string,
  endTime: PropTypes.string,
  newStatus: PropTypes.string,
  offlineReason: PropTypes.string,
  description: PropTypes.string,
};

export default ChangeVehicleStatusBulkReservationsConflictModal;
